import { useEffect, useMemo, useState } from "react";

import { useDeployment, useWallet } from "../../context";
import { V3_FACTORY_ABI, V3_POOL_EXTENDED_ABI } from "../../lib/abis";
import { ZERO_ADDRESS } from "../../lib/constants";

import type { Address } from "viem";

interface TickBucket {
  tick: number;
  liquidity: number;
}

/**
 * Computes cumulative active liquidity across tick buckets for V3 pools.
 *
 * Root cause of the old broken histogram: reading `liquidityGross` from
 * `ticks()` returns 0 at uninitialized ticks. In devenv, full-range
 * positions only initialize boundary ticks (-887220/887220), so all
 * sampled ticks around current price returned 0.
 *
 * Fix: Read pool's active `liquidity()` and walk outward, adjusting by
 * `liquidityNet` at initialized ticks. This produces a staircase showing
 * actual liquidity depth at each tick bucket.
 */
export function useLiquidityHistogram(poolTicks: Record<string, number>) {
  const { deployment } = useDeployment();
  const { publicClient } = useWallet();

  const [buckets, setBuckets] = useState<TickBucket[]>([]);
  const [loading, setLoading] = useState(false);

  const poolTicksKey = useMemo(() => JSON.stringify(poolTicks), [poolTicks]);

  useEffect(() => {
    if (!deployment?.v3.factory || Object.keys(poolTicks).length === 0) return;
    let cancelled = false;

    async function loadHistogram() {
      if (!deployment) return;
      setLoading(true);
      try {
        // Use the first pool that has a current tick
        const firstKey = Object.keys(poolTicks)[0];
        if (!firstKey) return;
        const [t0, t1, feeStr] = firstKey.split(":");
        const currentTick = poolTicks[firstKey];
        if (currentTick === undefined) return;

        // Get pool address
        const poolAddr = await publicClient.readContract({
          address: deployment.v3.factory,
          abi: V3_FACTORY_ABI,
          functionName: "getPool",
          args: [t0 as Address, t1 as Address, parseInt(feeStr ?? "")],
        });

        if (poolAddr === ZERO_ADDRESS) return;

        // Get tick spacing
        const spacing = await publicClient.readContract({
          address: poolAddr,
          abi: V3_POOL_EXTENDED_ABI,
          functionName: "tickSpacing",
        });

        // Read pool's current active liquidity
        const poolLiquidity = await publicClient.readContract({
          address: poolAddr,
          abi: V3_POOL_EXTENDED_ABI,
          functionName: "liquidity",
        });

        // Sample 50 tick spacings each direction to capture concentrated tiers
        const numSamples = 50;
        const alignedCurrent = Math.floor(currentTick / spacing) * spacing;

        // Read all ticks in parallel (collapses into 1 multicall with batching)
        const ticks = Array.from(
          { length: 2 * numSamples + 1 },
          (_, i) => alignedCurrent + (i - numSamples) * spacing,
        );

        const tickResults = await Promise.allSettled(
          ticks.map((tick) =>
            publicClient.readContract({
              address: poolAddr,
              abi: V3_POOL_EXTENDED_ABI,
              functionName: "ticks",
              args: [tick],
            }),
          ),
        );

        const tickData = tickResults.map((r, i) => {
          if (r.status === "fulfilled") {
            const result = r.value as unknown as readonly [
              bigint,
              bigint,
              bigint,
              bigint,
              bigint,
              bigint,
              bigint,
              boolean,
            ];
            return {
              tick: ticks[i] as number,
              liquidityNet: result[1],
              initialized: result[7],
            };
          }
          return {
            tick: ticks[i] as number,
            liquidityNet: 0n,
            initialized: false,
          };
        });

        // Walk outward from current tick: starting with pool's active
        // liquidity, subtract liquidityNet at initialized ticks going
        // left, add going right. This produces the staircase.
        const result: TickBucket[] = [];

        // Find the index of the aligned current tick
        const centerIdx = tickData.findIndex((d) => d.tick === alignedCurrent);

        // Walk left from center (decreasing ticks)
        let liqLeft = poolLiquidity;
        for (let i = centerIdx; i >= 0; i--) {
          const d = tickData[i];
          if (!d) continue;
          if (i < centerIdx && d.initialized) {
            // When walking left, we cross ticks from right to left,
            // so we subtract liquidityNet
            liqLeft -= d.liquidityNet;
          }
          result.unshift({
            tick: d.tick,
            liquidity: Number(liqLeft < 0n ? 0n : liqLeft),
          });
        }

        // Walk right from center+1 (increasing ticks)
        let liqRight = poolLiquidity;
        for (let i = centerIdx + 1; i < tickData.length; i++) {
          const d = tickData[i];
          if (!d) continue;
          if (d.initialized) {
            // When walking right, we cross ticks from left to right,
            // so we add liquidityNet
            liqRight += d.liquidityNet;
          }
          result.push({
            tick: d.tick,
            liquidity: Number(liqRight < 0n ? 0n : liqRight),
          });
        }

        if (!cancelled) {
          setBuckets(result);
        }
      } catch {
        // Histogram loading is best-effort
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadHistogram();
    return () => {
      cancelled = true;
    };
  }, [deployment?.v3.factory, poolTicksKey, publicClient]);

  return { buckets, loading };
}
